import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { endpoints } from '@/api';
import { Icon } from '@/components/ui/Icon';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { AdPlaceholder } from './AdPlaceholder';
import { getAdProvider } from './runtime';
import { localDay, safeStorage } from './storage';
import type { CardPlacement, DisplayHandle } from './types';
import { useAdsGate } from './useAds';

const dismissKey = (p: CardPlacement) => `popin.ads.dismissed.${p}`;

export function dismissedToday(p: CardPlacement, now = Date.now()): boolean {
  return safeStorage.get(dismissKey(p)) === localDay(now);
}

type SlotState = 'loading' | 'filled' | 'placeholder' | 'empty';

/**
 * `<AdSlot placement>`: one display slot. Renders only when every gate passes
 * (ads on, eligible, not Plus, not an excluded screen); empty fills collapse.
 */
export function AdSlot({ placement, onState }: { placement: CardPlacement; onState?: (s: SlotState) => void }) {
  const gate = useAdsGate();
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<SlotState>('loading');
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const ads = gate.here ? gate.ads : null;
  const unit = ads?.units[placement] ?? '';

  useEffect(() => {
    if (!ads || !unit || !ref.current) return;
    let alive = true;
    let handle: DisplayHandle | null = null;
    const el = ref.current;
    const update = (s: SlotState) => {
      if (!alive) return;
      setState(s);
      onStateRef.current?.(s);
    };
    void getAdProvider(ads)
      .then((p) => (p ? p.renderDisplay(placement, el) : null))
      .then((h) => {
        handle = h;
        if (!alive) return h?.destroy();
        update(!h || !h.filled ? 'empty' : h.placeholder ? 'placeholder' : 'filled');
      })
      .catch(() => update('empty'));
    return () => {
      alive = false;
      handle?.destroy();
    };
  }, [ads, unit, placement]);

  if (!ads || !unit || state === 'empty') return null;
  return (
    <div className="relative w-full overflow-hidden" data-ad-slot={placement}>
      {state === 'loading' && <Skeleton className="h-[100px] w-full" rounded="rounded-[12px]" />}
      {state === 'placeholder' && <AdPlaceholder />}
      {/* GPT renders into this div. */}
      <div ref={ref} className={cn('flex justify-center', state !== 'filled' && 'h-0 overflow-hidden')} />
    </div>
  );
}

/**
 * Native "Sponsored" card (SPEC_V5 §4): same visual language as the app,
 * clearly labelled, dismissable for the day. Impressions → POST /v1/ads/impressions.
 */
export function SponsoredCard({ placement, className, as = 'div' }: { placement: CardPlacement; className?: string; as?: 'div' | 'li' }) {
  const gate = useAdsGate();
  const [hidden, setHidden] = useState(() => dismissedToday(placement));
  const [empty, setEmpty] = useState(false);
  const recorded = useRef(false);

  if (!gate.here || !gate.ads?.units[placement] || hidden || empty) return null;

  const onState = (s: SlotState) => {
    if (s === 'empty') setEmpty(true);
    if ((s === 'filled' || s === 'placeholder') && !recorded.current) {
      recorded.current = true;
      void endpoints.adsImpression(placement).catch(() => undefined);
    }
  };
  const dismiss = () => {
    safeStorage.set(dismissKey(placement), localDay());
    setHidden(true);
    void endpoints.trackEvent({ name: 'ad_card_dismissed', props: { placement } }).catch(() => undefined);
  };
  const Tag = as === 'li' ? motion.li : motion.div;
  return (
    <Tag
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={cn('flex flex-col rounded-card bg-surface p-3', className)}
      data-testid="sponsored-card"
      data-placement={placement}
      aria-label="Sponsored"
    >
      <div className="mb-2 flex items-center justify-between gap-2 pl-2">
        <span className="text-[13px] font-medium text-muted">Sponsored</span>
        <button
          type="button"
          onClick={dismiss}
          className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-[13px] font-medium text-muted transition-colors hover:bg-raised hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg"
          aria-label="Hide sponsored card for today"
          data-testid="sponsored-dismiss"
        >
          Hide for today <Icon name="x" size={16} />
        </button>
      </div>
      <AdSlot placement={placement} onState={onState} />
    </Tag>
  );
}
